import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import HubPageLayout from '../components/HubPageLayout';
import { BackToTopLink } from '../components/BackToTop';
import HubArticleFrame from '../components/HubArticleFrame';
import { setDashboardTitle } from '../pageTitles';
import AlertBody from '../FraudAlerts/components/AlertBody';
import { isEmptyHtml } from '../FraudAlerts/alertBodyFormat';
import './hotScamBody.css';
import '../FraudAlerts/components/alertBody.css';

// Hot Scam page. The framed header with its sub-tabs is HubArticleFrame
// (shared with the agency alert pages since 10-28); the body is two
// free-form HTML boxes written in the admin editor - what_to_know above the
// section image, know_more below it. Headings and lists come from the
// author; the grey tiles and fixed numbered sections are gone (their text is
// kept in hot-scams-tile-archive.md at the repo root).

// The page itself, minus data loading. Shared with the admin's full-page
// preview so what a reviewer approves is what readers get. `openPanel` /
// `onTogglePanel` let the preview stay inert.
export const HotScamArticle = ({ scam, openPanel = null, onTogglePanel = () => {} }) => (
  <>
    <HubArticleFrame
      title={scam.title || <span className="text-gray-400">Untitled hot scam</span>}
      current={{ kind: 'hot-scam', slug: scam.slug }}
      openPanel={openPanel}
      onTogglePanel={onTogglePanel}
    >
      {scam.hero_image_url && (
        <img
          src={scam.hero_image_url}
          alt={scam.title}
          className="w-full max-h-[340px] object-cover"
        />
      )}
      {scam.hero_caption && (
        <p className="text-lg text-black mt-4">{scam.hero_caption}</p>
      )}
    </HubArticleFrame>

    {/* Text box 1 - above the section image */}
    {!isEmptyHtml(scam.what_to_know) && (
      <div className="mt-10 hot-scam-body">
        <AlertBody html={scam.what_to_know} />
      </div>
    )}

    {scam.know_more_image_url && (
      <img
        src={scam.know_more_image_url}
        alt=""
        className="w-full max-h-[340px] object-cover mt-10"
      />
    )}

    {/* Text box 2 - below the section image */}
    {!isEmptyHtml(scam.know_more) && (
      <div className="mt-10 hot-scam-body">
        <AlertBody html={scam.know_more} />
      </div>
    )}
    <BackToTopLink />
  </>
);

const HotScamDetailPage = () => {
  const { slug } = useParams();
  const [state, setState] = useState({ scam: null, loading: true, error: false });
  const [openPanel, setOpenPanel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ scam: null, loading: true, error: false });
    setOpenPanel(null);

    fetch(`${API_BASE_URL}/bff/hot-scams/${slug}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!cancelled) setState({ scam: d.scam, loading: false, error: false }); })
      .catch(() => { if (!cancelled) setState({ scam: null, loading: false, error: true }); });

    return () => { cancelled = true; };
  }, [slug]);

  const { scam, loading, error } = state;

  // Tab title: the scam's own name once loaded (the layout set "Hot Scams").
  useEffect(() => {
    if (scam?.title) setDashboardTitle(scam.title);
  }, [scam?.title]);

  return (
    <HubPageLayout>
      {/* Explicit way back to the hub. The sub-tab panels here navigate off
          to Av Library / MoSAIC / Detection pages, so a reader can arrive
          with no useful browser history to go back through. ?tab=hot-scams
          returns them to the tab they came from, not the FBI/IC3 default. */}
      <Link
        to="/dashboard/fraud-alerts?tab=hot-scams"
        className="inline-flex items-center gap-2 mt-4 text-sm text-black hover:text-blue-600 hover:underline"
      >
        <span aria-hidden="true">&larr;</span> Back to CyberFraud Alert
      </Link>

      {loading && <p className="py-12 text-center text-gray-400">Loading&hellip;</p>}
      {error && (
        <div className="py-12 text-center">
          <p className="text-gray-500">Couldn&rsquo;t load this scam page.</p>
          <Link to="/dashboard/fraud-alerts" className="text-blue-600 hover:underline">
            Back to CyberFraud Alert
          </Link>
        </div>
      )}

      {scam && <HotScamArticle scam={scam} openPanel={openPanel} onTogglePanel={setOpenPanel} />}
    </HubPageLayout>
  );
};

export default HotScamDetailPage;
